import { LANGUAGE_REGEX, REGEX_MARKDOWN_CODE_REMOVES_NEWLINE } from "../../constants/regexValues";
import { useTheme } from "../../context/ThemeProvider";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { useCodeBlockSave } from "../../context/CodeBlockSaveContext";
import { getCodeBlockIndex, replaceCodeBlockContent } from "../../utils/codeBlockUtils";
import useCopyToClipboard from "../../hooks/useCopyToClipboard";
import useArtifactSave from "../../hooks/useArtifactSave";
import useInlineEdit from "../../hooks/useInlineEdit";
import { useSparkContext } from "../../context/SparkContext";
import VizCopyButton from "../shared/Viz/VizCopyButton";
import { SaveButton, ResetButton, PreviewCodeToggle } from "../shared/EditorControls";
import InlineEditDiffView from "../shared/InlineEditDiffView";
import EditorModelSelector from "../Nebula/EditorModelSelector";
import { ArrowUp, Square, X, Check, GitCompare } from "lucide-react";
import { diffArtifacts } from "../../utils/nebulaArtifactDiff";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import "@fontsource/source-code-pro/400.css";
import MermaidDiagram from "./MermaidDiagram";
import MarkmapDiagram from "./MarkmapDiagram";
import PhysicsViz from "./PhysicsViz";
import ReactViz from "./ReactViz";
import HtmlViz from "./HtmlViz";
import CodeMirror from "@uiw/react-codemirror";
import { vscodeDark, vscodeLight } from "@uiw/codemirror-theme-vscode";
import { getCmExtensions, extractDiagnostics } from "../../utils/codeMirrorExtensions";


const CodeBlock = ({ inline, className, children = "" }) => {
  const codeRef = useRef(null);
  const { theme } = useTheme();
  const cmTheme = theme === "dark" ? vscodeDark : vscodeLight;
  const { copied, copyToClipboard } = useCopyToClipboard({ caller: "CodeBlock" });
  const { answerId, chatOwnerId, rawContent, isStreaming } = useCodeBlockSave();
  const [showCode, setShowCode] = useState(false);
  const [isStreamedPreviewReady, setIsStreamedPreviewReady] = useState(!isStreaming);

  const language = LANGUAGE_REGEX.exec(className || "");
  const langTag = language ? language[1] : "";
  const code = String(children).replace(
    REGEX_MARKDOWN_CODE_REMOVES_NEWLINE,
    ""
  );

  // Editable code state — editableCode drives the textarea
  // appliedCode drives the viz (live for light viz, manual "Run" for heavy)
  const [editableCode, setEditableCode] = useState(code);
  const [appliedCode, setAppliedCode] = useState(code);
  const [hasLintErrors, setHasLintErrors] = useState(false);
  // Full diagnostics array — { message, severity, line, col }[].
  // hasLintErrors is derived on each update (any error-severity entry).
  const [diagnostics, setDiagnostics] = useState([]);
  // hasUserEdited tracks whether the user has actually typed in the editor.
  // Unlike `editableCode !== code`, this stays false during streaming (where
  // `code` advances but the user hasn't touched anything), so displayCode
  // correctly tracks the live prop until the user makes a real edit.
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const isDirty = hasUserEdited;
  const [savedBase, setSavedBase] = useState(code);

  // ── Inline AI editor state ────────────────────────────────────────
  const { chat_id } = useSparkContext();
  const [editPrompt, setEditPrompt] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editError, setEditError] = useState(null);
  const [editorModelId, setEditorModelId] = useState(null);
  const [diffPending, setDiffPending] = useState(false);
  const [showSavedDiff, setShowSavedDiff] = useState(false);
  const preEditSnapshotRef = useRef(null);
  const preEditDiagnosticsRef = useRef([]);
  // Captures the full AI-generated code at stream completion — avoids stale closure in diff memo
  const postEditCodeRef = useRef(null);
  // Tracks whether the most recent unsaved change came from an accepted AI edit
  const aiEditAcceptedRef = useRef(false);

  const { startEdit, cancel: cancelEdit } = useInlineEdit({
    onChunk: (buf) => { setEditableCode(buf); },
    onComplete: (full) => {
      postEditCodeRef.current = full;
      setEditableCode(full);
      if (!isEditing) return; // already cancelled
      setHasUserEdited(true);
      setIsEditing(false);
      setEditPrompt("");
      setEditError(null);
      setDiffPending(true);
    },
    onError: (msg) => { setEditError(msg); setIsEditing(false); },
  });

  const handleEditSubmit = useCallback(() => {
    if (!editPrompt.trim() || isEditing) return;
    preEditSnapshotRef.current = editableCode;
    preEditDiagnosticsRef.current = diagnostics;
    setEditError(null);
    setIsEditing(true);
    startEdit({
      prompt: editPrompt,
      currentCode: editableCode,
      language: langTag || "html",
      diagnostics,
      answerId,
      chatId: chat_id,
      modelId: editorModelId,
    });
  }, [editPrompt, isEditing, editableCode, diagnostics, langTag, answerId, chat_id, editorModelId, startEdit]);

  const handleAcceptEdit = useCallback(() => {
    setDiffPending(false);
    aiEditAcceptedRef.current = true;
    preEditSnapshotRef.current = null;
    postEditCodeRef.current = null;
    preEditDiagnosticsRef.current = [];
  }, []);

  const handleRejectEdit = useCallback(() => {
    const restored = preEditSnapshotRef.current;
    if (restored !== null) {
      setEditableCode(restored);
      setAppliedCode(restored);
      setHasUserEdited(restored !== savedBase);
    }
    setDiffPending(false);
    aiEditAcceptedRef.current = false;
    preEditSnapshotRef.current = null;
    postEditCodeRef.current = null;
    preEditDiagnosticsRef.current = [];
  }, [savedBase]);

  const inlineEditDiffLines = useMemo(() => {
    if (!diffPending || preEditSnapshotRef.current === null || postEditCodeRef.current === null) return [];
    return diffArtifacts(preEditSnapshotRef.current, postEditCodeRef.current);
  }, [diffPending]);

  const errorDeltaCount = diffPending
    ? diagnostics.filter(d => d.severity === "error").length
      - preEditDiagnosticsRef.current.filter(d => d.severity === "error").length
    : 0;

  // C2 — savedBase diff: shows unsaved changes when user toggles the GitDiff button
  const savedDiffLines = useMemo(() => {
    if (!showSavedDiff || !isDirty) return [];
    return diffArtifacts(savedBase, editableCode);
  }, [showSavedDiff, isDirty, savedBase, editableCode]);
  const showDiffView = diffPending || showSavedDiff;
  const activeDiffLines = diffPending ? inlineEditDiffLines : savedDiffLines;
  useEffect(() => {
    if (!isDirty) setShowSavedDiff(false);
  }, [isDirty]);

  // Prevent navigation mid-edit
  useEffect(() => {
    if (!isEditing) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isEditing]);

  // While streaming (code prop advances) and user hasn't edited, keep local
  // state in sync so the editor and preview always show the latest content.
  useEffect(() => {
    if (!hasUserEdited) {
      setSavedBase(code);
      setEditableCode(code);
      setAppliedCode(code);
    }
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isStreaming) {
      setIsStreamedPreviewReady(false);
      return undefined;
    }

    const frameId = window.requestAnimationFrame(() => setIsStreamedPreviewReady(true));
    return () => window.cancelAnimationFrame(frameId);
  }, [code, isStreaming]);
  const blockIndex = useMemo(
    () => (rawContent ? getCodeBlockIndex(rawContent, langTag, code) : 0),
    [rawContent, langTag, code]
  );
  const buildUpdatedContent = useCallback(
    (edited) => replaceCodeBlockContent(rawContent || "", langTag, blockIndex, edited),
    [rawContent, langTag, blockIndex]
  );
  const { saveStatus, save: persistSave } = useArtifactSave({ answerId, chatOwnerId, buildUpdatedContent });

  const handleSave = async () => {
    const trigger = aiEditAcceptedRef.current ? "inline_ai_edit" : "manual_edit";
    const persisted = await persistSave(editableCode, trigger);
    if (persisted != null) {
      setSavedBase(editableCode);
      setAppliedCode(editableCode);
      setHasUserEdited(false);
      aiEditAcceptedRef.current = false;
    }
  };

  // Check if this is a mermaid diagram
  const isMermaid = language && language[1] === 'mermaid';
  // Check if this is a markmap mind map
  const isMarkmap = language && language[1] === 'markmap';
  // Check if this is a physics visualization
  const isPhysicsViz = language && (language[1] === 'physics-viz' || language[1] === 'json-viz');
  // Check if this is a React visualization
  const isReactViz = language && (language[1] === 'react-viz' || language[1] === 'jsx-live');
  // Check if this is an HTML visualization
  const isHtmlViz = language && (language[1] === 'html-viz' || language[1] === 'html-live' || language[1] === 'html');
  
  // Wraps any viz with a Preview | Code pill toggle
  // renderViz: (code: string) => JSX — called with the appropriate code version
  // isHeavy: true for ReactViz/HtmlViz — preview only updates on explicit "Run"
  const wrapViz = (renderViz, vizLanguage, isHeavy = false) => {
    // When the user hasn't manually edited, use the live prop value (`code`) so
    // the preview and editor always reflect the fully-streamed content without
    // needing a Reset. When dirty, show appliedCode (last explicitly Run/applied
    // version) so the preview doesn't flicker while the user is mid-edit.
    const displayCode = hasUserEdited ? appliedCode : savedBase;

    // Force the editor view while the parent message is streaming. Heavy viz
    // (React/HTML/Physics) and diagram parsers re-process the entire partial
    // source on every chunk, producing visual corruption and wasted CPU. Mount
    // the renderer exactly once when the stream completes with the final source.
    const effectiveShowCode = isStreaming || (isHeavy && !isStreamedPreviewReady)
      ? true
      : showCode;

    const toggleEl = (
      <div className="flex items-center gap-2">
        <PreviewCodeToggle showCode={effectiveShowCode} onToggle={setShowCode} disabled={isStreaming} />
        {(isStreaming || (isHeavy && !isStreamedPreviewReady)) && (
          <span className="text-[10px] uppercase tracking-wider text-ink-gray-500 dark:text-ink-gray-400 animate-pulse">
            Streaming…
          </span>
        )}
      </div>
    );

    return (
      <div className="my-3">
        {effectiveShowCode ? (
          <div>
            {/* Header bar: toggle on left, action buttons on right */}
            <div className="flex items-center justify-between px-2 py-1.5 rounded-t-md border-x border-t border-edge-hex-e2e8f0 dark:border-edge-editor-reset-border bg-fill-white dark:bg-fill-canvas-page-surface">
              {toggleEl}
              <div className="flex items-center gap-1.5">
                {isDirty && (
                  <ResetButton
                    disabled={diffPending}
                    onReset={() => {
                      setHasUserEdited(false);
                      setEditableCode(savedBase);
                      setAppliedCode(savedBase);
                      setDiffPending(false);
                      preEditSnapshotRef.current = null;
                      preEditDiagnosticsRef.current = [];
                    }}
                  />
                )}
                {isDirty && answerId && (
                  <SaveButton status={saveStatus} onSave={handleSave} hasErrors={hasLintErrors} disabled={diffPending} />
                )}
                {isDirty && !diffPending && (
                  <button
                    onClick={() => setShowSavedDiff(v => !v)}
                    title={showSavedDiff ? "Close changes diff" : "Show unsaved changes"}
                    className={`p-1 rounded transition-colors ${showSavedDiff ? "bg-fill-canvas-accent/20 text-ink-canvas-accent" : "text-ink-gray-500 dark:text-ink-gray-400 hover:text-ink-canvas-accent"}`}
                  >
                    <GitCompare className="w-3.5 h-3.5" />
                  </button>
                )}
                {isHeavy && (
                  <button
                    disabled={hasLintErrors || editableCode === appliedCode}
                    onClick={() => { setAppliedCode(editableCode); setShowCode(false); }}
                    title={hasLintErrors ? "Fix errors before running" : editableCode === appliedCode ? "No changes to run" : "Run and preview"}
                    className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium transition-opacity ${
                      hasLintErrors || editableCode === appliedCode
                        ? 'bg-fill-gray-300 dark:bg-fill-strong-border text-ink-gray-400 dark:text-ink-gray-500 cursor-not-allowed'
                        : 'bg-fill-ink dark:bg-fill-editor-surface text-ink-white hover:opacity-80'
                    }`}
                  >
                    ▶ Run
                  </button>
                )}
                <VizCopyButton
                  copied={copied}
                  onClick={() => copyToClipboard(hasUserEdited ? editableCode : code)}
                  tooltip="Copy code"
                  className="opacity-100"
                />
              </div>
            </div>
            {/* Inline AI prompt row or Accept/Reject gate */}
            {diffPending ? (
              <div className="flex items-center gap-2 px-2 py-1.5 border-x border-edge-hex-e2e8f0 dark:border-edge-editor-reset-border bg-fill-white dark:bg-fill-canvas-page-surface">
                <span className="flex-1 min-w-0 text-xs text-ink-gray-500 dark:text-ink-gray-400 truncate">Review AI changes</span>
                {errorDeltaCount !== 0 && (
                  <span className={`flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                    errorDeltaCount > 0
                      ? "bg-fill-red-100 dark:bg-fill-red-900/30 text-ink-red-800 dark:text-ink-red-300"
                      : "bg-fill-green-100 dark:bg-fill-green-900/30 text-ink-green-800 dark:text-ink-green-300"
                  }`}>
                    {errorDeltaCount > 0 ? `+${errorDeltaCount}` : `${errorDeltaCount}`} errors
                  </span>
                )}
                <button onClick={handleAcceptEdit} title="Accept AI edit"
                  className="flex-shrink-0 flex items-center gap-1 text-[11px] px-2 py-1 rounded-md font-medium bg-fill-brand-openai/10 dark:bg-fill-brand-openai/20 text-ink-brand-openai border border-edge-brand-openai/30 hover:bg-fill-brand-openai/25 transition-colors">
                  <Check className="w-3 h-3" /> Accept
                </button>
                <button onClick={handleRejectEdit} title="Reject AI edit"
                  className="flex-shrink-0 flex items-center gap-1 text-[11px] px-2 py-1 rounded-md font-medium bg-fill-red-50 dark:bg-fill-red-900/20 text-ink-red-800 border border-edge-red-200 dark:border-edge-red-800 hover:bg-fill-red-100 dark:hover:bg-fill-red-900/30 transition-colors dark:text-ink-red-300">
                  <X className="w-3 h-3" /> Reject
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 px-2 py-1 border-x border-edge-hex-e2e8f0 dark:border-edge-editor-reset-border bg-fill-white dark:bg-fill-canvas-page-surface">
                <EditorModelSelector language={vizLanguage} chatId={chat_id} onChange={setEditorModelId} />
                <input
                  className="photon-field flex-1 text-xs rounded-md px-2 py-1 bg-fill-gray-100 dark:bg-fill-panel-surface border border-edge-border dark:border-edge-strong-border text-ink-ink dark:text-ink-white placeholder-ink-gray-400 dark:placeholder-ink-gray-500 focus:outline-none focus:ring-1 focus:ring-focus-canvas-accent disabled:opacity-50"
                  placeholder="AI edit… e.g. make the button red"
                  value={editPrompt}
                  disabled={isEditing}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleEditSubmit(); } }}
                />
                {isEditing ? (
                  <button onClick={cancelEdit} title="Stop"
                    className="flex items-center justify-center w-7 h-7 rounded-full bg-fill-red-500/10 hover:bg-fill-red-500/20 text-ink-red-400 transition-colors">
                    <Square className="w-3 h-3 fill-current" />
                  </button>
                ) : (
                  <button onClick={handleEditSubmit} disabled={!editPrompt.trim()} title="Apply AI edit"
                    className="photon-primary-action flex items-center justify-center w-7 h-7 rounded-full bg-button-primary text-button-on-primary transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                    <ArrowUp className="w-3.5 h-4" />
                  </button>
                )}
                {editError && (
                  <span className="text-xs text-ink-red-400 truncate max-w-[140px]" title={editError}>{editError}</span>
                )}
              </div>
            )}
            {/* Resizable CodeMirror or diff view */}
            <div
              style={{
                resize: showDiffView ? "none" : "vertical",
                overflow: "hidden",
                height: "400px",
                maxHeight: "80vh",
                borderRadius: "0 0 0.375rem 0.375rem",
                border: theme === "dark" ? "1px solid rgb(var(--photon-edge-code-dark))" : "1px solid rgb(var(--photon-edge-hex-e2e8f0))",
              }}
            >
              {showDiffView ? (
                <InlineEditDiffView lines={activeDiffLines} />
              ) : (
                <CodeMirror
                  value={isDirty ? editableCode : savedBase}
                  theme={cmTheme}
                  extensions={getCmExtensions(vizLanguage, true, theme === "dark")}
                  height="100%"
                  onChange={(val) => {
                    setHasUserEdited(true);
                    setEditableCode(val);
                    if (!isHeavy) setAppliedCode(val);
                    aiEditAcceptedRef.current = false;
                  }}
                  onUpdate={(update) => {
                    const next = extractDiagnostics(update.state);
                    setDiagnostics(next);
                    setHasLintErrors(next.some(d => d.severity === "error"));
                  }}
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: false,
                    dropCursor: false,
                    allowMultipleSelections: false,
                    indentOnInput: true,
                    syntaxHighlighting: true,
                    bracketMatching: true,
                    autocompletion: true,
                    closeBrackets: true,
                  }}
                  style={{
                    fontSize: "13px",
                    height: "100%",
                  }}
                  className="rounded-b-md"
                />
              )}
            </div>
          </div>
        ) : renderViz(displayCode, toggleEl)}
      </div>
    );
  };

  // If it's a mermaid diagram, render the MermaidDiagram component
  if (isMermaid) {
    return wrapViz((c, t) => <MermaidDiagram chart={c} toggleElement={t} />, 'mermaid');
  }

  // If it's a markmap mind map, render the MarkmapDiagram component
  if (isMarkmap) {
    return wrapViz((c, t) => <MarkmapDiagram chart={c} toggleElement={t} />, 'markdown');
  }

  // If it's a physics visualization, render the PhysicsViz component
  if (isPhysicsViz) {
    return wrapViz((c, t) => <PhysicsViz code={c} toggleElement={t} />, 'json');
  }

  // If it's a React visualization, render the ReactViz component
  if (isReactViz) {
    return wrapViz((c, t) => <ReactViz code={c} toggleElement={t} />, 'jsx', true);
  }

  // If it's an HTML visualization, render the HtmlViz component
  if (isHtmlViz) {
    return wrapViz((c, t) => <HtmlViz code={c} toggleElement={t} />, 'html', true);
  }

  const handleCopy = () => {
    const codeElement = codeRef.current?.querySelector("pre");
    const textToCopy = codeElement ? codeElement.textContent.trim() : String(children);
    copyToClipboard(textToCopy);
  };

  if (inline) {
    return (
      <code className="photon-code-inline">
        {children}
      </code>
    );
  }

  return (
    <figure
      className="photon-code-block relative"
      ref={codeRef}
      dir="ltr"
    >
      <div
        role="region"
        aria-label={`${langTag || "Text"} code block`}
        tabIndex={0}
        className="max-h-[600px] overflow-auto py-3.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-focus-primary"
      >
      <SyntaxHighlighter
        useInlineStyles={false}
        language={language ? language[1] : "text"}
        PreTag="pre"
        customStyle={{
          margin: 0,
          padding: "0 3rem 0 1rem",
          background: "transparent",
          minWidth: "100%",
          width: "max-content",
          maxWidth: "none",
          overflow: "visible",
          fontSize: "13px",
          lineHeight: "1.5",
          fontFamily: '"Source Code Pro", monospace',
          textShadow: "none",
        }}
        codeTagProps={{
          className: "text-ink-code dark:text-ink-code-dark",
          style: {
            fontFamily: "inherit",
            whiteSpace: "pre",
            wordBreak: "normal",
            overflowWrap: "normal",
          },
        }}
      >
        {code}
      </SyntaxHighlighter>
      </div>

      {/* Copy Button */}
      <VizCopyButton
        copied={copied}
        onClick={handleCopy}
        tooltip="Copy code"
        variant="ghost"
        className="absolute top-2.5 right-2 z-10 backdrop-blur-sm"
      />
    </figure>
  );
};

export default CodeBlock;
